import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Copy } from 'lucide-react';
import {
  Button, Badge, Checkbox, ConfirmDialog, IconButton, Select, ToggleGroup,
} from '@parsina/framework';
import { useTranslation } from '../../../i18n';
import { C } from '../../../constants/theme';
import { STACK } from '../../../constants/density.js';
import TimePicker from '../../../components/common/TimePicker.jsx';
import {
  PRESENCE_DAYS, PRESENCE_ISSUE, presenceIssues, nextPresenceRange, copyPresenceDay,
} from '../personnel.js';

/**
 * ساعات حضور — the weekly shifts a کادر درمان member works at THIS clinic.
 * Layout and behaviour: `docs/parsina_clinic_hours_wireframe.md`.
 *
 * This is the heart of the person↔clinic relationship: it is what makes them
 * bookable, and `scheduling` enforces it at booking time — a slot outside these
 * hours is refused, so an empty week means nobody can be given an appointment.
 * The rows live in `scheduling.doctor_presences`; the clinic writes them through
 * `updateDoctorLink({ presences })`, which REPLACES the whole week (a merge
 * would resurrect a shift the manager just deleted).
 *
 * `value` stays the flat wire list — the drawer's dirty flag and the mutation
 * both read it — and the editor only GROUPS it: one row per weekday, 0 = شنبه,
 * the same order `weekdayNames()` returns. Within a day, ranges keep their
 * insertion order rather than being sorted, so a row never jumps away from
 * under the cursor while its start time is being typed.
 *
 * «فعال» is derived from the data (a day with ranges is on) plus one piece of
 * view state: a day switched on, or whose last range was just deleted, stays
 * on with «بدون شیفت» until the manager turns it off. The wire has no notion of
 * an enabled day without hours, so that state is reset with `resetKey`.
 *
 * Validation is inline and non-blocking: the error sits under the range it is
 * about and the save is refused by the caller.
 */
/** One-tap boundaries in the picker — where a clinic shift nearly always starts or ends. */
const SHIFT_TIMES = ['07:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];

export default function PresenceEditor({ value = [], onChange, disabled = false, resetKey }) {
  const { t, weekdayNames } = useTranslation();
  const names = weekdayNames();

  const [openDays, setOpenDays] = useState(() => new Set());
  useEffect(() => { setOpenDays(new Set()); }, [resetKey]);

  const [copy, setCopy] = useState(null);           // { from, locked }
  const [disabling, setDisabling] = useState(null); // day index awaiting confirmation

  const issues = useMemo(() => presenceIssues(value), [value]);
  const byDay = useMemo(() => PRESENCE_DAYS.map((d) => value
    .map((slot, index) => ({ ...slot, index }))
    .filter((s) => Number(s.dayOfWeek) === d)), [value]);
  const daysWithHours = PRESENCE_DAYS.filter((d) => byDay[d].length > 0);

  const setOpen = (day, on) => setOpenDays((prev) => {
    const next = new Set(prev);
    if (on) next.add(day); else next.delete(day);
    return next;
  });

  const update = (i, patch) => onChange(value.map((slot, idx) => (idx === i ? { ...slot, ...patch } : slot)));
  const addRange = (day) => { setOpen(day, true); onChange([...value, nextPresenceRange(value, day)]); };
  const removeRange = (day, i) => { setOpen(day, true); onChange(value.filter((_, idx) => idx !== i)); };
  const clearDay = (day) => { setOpen(day, false); onChange(value.filter((s) => Number(s.dayOfWeek) !== day)); };

  const toggleDay = (day, on) => {
    if (on) { addRange(day); return; }
    if (byDay[day].length > 0) setDisabling(day); else setOpen(day, false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{t('people.presence.title')}</h3>
        {!disabled && (
          <Button
            variant="outline"
            size="sm"
            disabled={daysWithHours.length === 0}
            onClick={() => setCopy({ from: daysWithHours[0], locked: false })}
          >
            <Copy size={14} /> {t('people.presence.copyAll')}
          </Button>
        )}
      </div>

      {value.length === 0 && (
        <div style={{
          fontSize: 12.5, color: C.textMuted, lineHeight: 1.9,
          border: `1px dashed ${C.border}`, borderRadius: 10, padding: '10px 12px',
        }}>
          {t('people.presence.emptyBlocksBooking')}
        </div>
      )}

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.card }}>
        {PRESENCE_DAYS.map((day) => (
          <DayRow
            key={day}
            name={names[day]}
            ranges={byDay[day]}
            issues={issues}
            enabled={byDay[day].length > 0 || openDays.has(day)}
            disabled={disabled}
            last={day === PRESENCE_DAYS.length - 1}
            onToggle={(on) => toggleDay(day, on)}
            onAdd={() => addRange(day)}
            onRemove={(i) => removeRange(day, i)}
            onCopy={() => setCopy({ from: day, locked: true })}
            onUpdate={update}
          />
        ))}
      </div>

      {copy && (
        <CopyDialog
          names={names}
          from={copy.from}
          locked={copy.locked}
          sources={daysWithHours}
          byDay={byDay}
          onClose={() => setCopy(null)}
          onApply={(from, targets, mode) => {
            onChange(copyPresenceDay(value, from, targets, mode));
            setOpenDays((prev) => new Set([...prev, ...targets]));
            setCopy(null);
          }}
        />
      )}

      <ConfirmDialog
        open={disabling != null}
        onOpenChange={(open) => { if (!open) setDisabling(null); }}
        title={disabling != null ? t('people.presence.disable.title', { day: names[disabling] }) : ''}
        description={t('people.presence.disable.body')}
        confirmLabel={t('people.presence.disable.confirm')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={() => { clearDay(disabling); setDisabling(null); }}
      />
    </div>
  );
}

function DayRow({
  name, ranges, issues, enabled, disabled, last, onToggle, onAdd, onRemove, onCopy, onUpdate,
}) {
  const { t } = useTranslation();
  const count = ranges.length;

  return (
    <div
      role="group"
      aria-label={name}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 12px',
        borderBottom: last ? 'none' : `1px solid ${C.border}`,
        background: enabled ? 'transparent' : C.bg,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 40 }}>
        <span style={{
          minWidth: 64, fontSize: 13.5, fontWeight: 600,
          color: enabled ? C.textPrimary : C.textMuted,
        }}>
          {name}
        </span>
        <DaySwitch
          checked={enabled}
          disabled={disabled}
          label={t('people.presence.toggleDay', { day: name })}
          onChange={onToggle}
        />
        <span style={{ fontSize: 12, color: enabled ? C.textSecondary : C.textMuted }}>
          {enabled ? t('people.presence.enabled') : t('people.presence.disabled')}
        </span>
        <Badge variant="outline">{t('people.presence.shiftCount', { count })}</Badge>
        <span style={{ flex: 1 }} />
        {!disabled && count > 0 && (
          <IconButton
            variant="ghost"
            size={40}
            icon={<Copy size={15} />}
            label={t('people.presence.copyDay', { day: name })}
            onClick={onCopy}
          />
        )}
      </div>

      {enabled && ranges.map((slot, n) => {
        const issue = issues[slot.index];
        const errorId = issue ? `presence-err-${slot.index}` : undefined;
        return (
          <div key={slot.index} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <TimePicker
                suggestions={SHIFT_TIMES}
                value={slot.startTime}
                disabled={disabled}
                invalid={Boolean(issue)}
                describedBy={errorId}
                label={t('people.presence.startOf', { day: name, n: n + 1 })}
                onChange={(v) => onUpdate(slot.index, { startTime: v })}
              />
              <span aria-hidden style={{ color: C.textMuted }}>—</span>
              <TimePicker
                suggestions={SHIFT_TIMES}
                value={slot.endTime}
                disabled={disabled}
                invalid={Boolean(issue)}
                describedBy={errorId}
                label={t('people.presence.endOf', { day: name, n: n + 1 })}
                onChange={(v) => onUpdate(slot.index, { endTime: v })}
              />
              {!disabled && (
                <IconButton
                  variant="ghost"
                  size={40}
                  icon={<Trash2 size={15} />}
                  label={t('people.presence.removeRange', { day: name, start: slot.startTime, end: slot.endTime })}
                  onClick={() => onRemove(slot.index)}
                />
              )}
            </div>
            {issue && (
              <div id={errorId} role="alert" style={{ fontSize: 12, color: C.red }}>
                {issue === PRESENCE_ISSUE.ORDER ? t('people.presence.errorOrder') : t('people.presence.errorOverlap')}
              </div>
            )}
          </div>
        );
      })}

      {enabled && !disabled && (
        <div>
          <Button variant="ghost" size="sm" onClick={onAdd} aria-label={t('people.presence.addRangeFor', { day: name })}>
            <Plus size={14} /> {t('people.presence.addRange')}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * «کپی ساعات» — one dialog for both doors. From a day's own copy button the
 * source is fixed; from the header the manager picks it. Targets default to
 * every other day, and «جایگزین کردن» is the default mode because «همه روزها
 * مثل شنبه» is what the button is pressed for — the dialog itself is the
 * confirmation, and it names the consequence when a target already has hours.
 */
function CopyDialog({ names, from: initialFrom, locked, sources, byDay, onClose, onApply }) {
  const { t } = useTranslation();
  const [from, setFrom] = useState(initialFrom);
  const [targets, setTargets] = useState(() => PRESENCE_DAYS.filter((d) => d !== initialFrom));
  const [mode, setMode] = useState('replace');

  const chosen = targets.filter((d) => d !== from);
  const overwrites = mode === 'replace' && chosen.some((d) => byDay[d].length > 0);

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={locked ? t('people.presence.copy.titleDay', { day: names[from] }) : t('people.presence.copy.title')}
      confirmLabel={t('common.actions.apply')}
      cancelLabel={t('common.actions.cancel')}
      confirmDisabled={chosen.length === 0}
      onConfirm={() => onApply(from, chosen, mode)}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
        {!locked && (
          <Select
            label={t('people.presence.copy.source')}
            placeholder={t('common.form.chooseEllipsis')}
            value={String(from)}
            onChange={(e) => setFrom(Number(e.target.value))}
            options={sources.map((d) => ({ value: String(d), label: names[d] }))}
          />
        )}
        <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <legend style={{ fontSize: 12.5, color: C.textSecondary, marginBottom: 6 }}>
            {t('people.presence.copy.targets')}
          </legend>
          {PRESENCE_DAYS.filter((d) => d !== from).map((d) => (
            <Checkbox
              key={d}
              label={names[d]}
              checked={targets.includes(d)}
              onChange={(on) => setTargets((prev) => (on ? [...prev, d] : prev.filter((x) => x !== d)))}
            />
          ))}
        </fieldset>
        <ToggleGroup
          label={t('people.presence.copy.mode')}
          value={mode}
          onChange={(v) => { if (v) setMode(v); }}
          options={[
            { value: 'replace', label: t('people.presence.copy.replace') },
            { value: 'append', label: t('people.presence.copy.append') },
          ]}
        />
        {overwrites && (
          <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.8 }}>
            {t('people.presence.copy.overwriteWarning')}
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
}

/**
 * The day's on/off control. The kit `Switch` is a `<div>` no keyboard can
 * reach and `Toggle` brings a full labelled row with its own divider, so this
 * is the kit's switch geometry on a real `<button role="switch">` — Tab, Space
 * and Enter work, and `aria-checked` rather than colour carries the state.
 */
function DaySwitch({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 999, padding: 0, flexShrink: 0, position: 'relative',
        border: 'none', background: checked ? C.navy : '#d1d5db',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
        transition: 'background .2s',
      }}
    >
      <span style={{
        position: 'absolute', top: 3, width: 16, height: 16, borderRadius: '50%',
        background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        right: checked ? 3 : 21, transition: 'right .2s',
      }} />
    </button>
  );
}

